import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cls from './UserDropdown.module.scss';
import { ReactComponent as SettignsIcon } from 'src/assets/img/icons/settings-dropdown-icon.svg';
import { ReactComponent as PowerIcon } from 'src/assets/img/icons/power-icon.svg';
import UserSettingsPopup from '../UserSettingsPopup/UserSettingsPopup';
import { useLogoutMutation } from 'src/services/Auth';
import { clearStore } from 'src/redux/Auth';
import { useTypedDispatch } from 'src/hooks/useTypedDispatch';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import selectors from 'src/redux/selectors';
import getBrowserVersion from 'src/utils/helpers/getBrowserVersion';
import getOC from 'src/utils/helpers/getOC';
import { getAuthTokens } from 'src/utils/helpers/authHelper';
import { PAGES_ENUM } from 'src/routes';
import { NavLink, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useSounds from 'src/hooks/useSounds';
import useEnumTranslate from 'src/hooks/translate/useEnumTranslate';
import { UserIcon } from 'src/assets/icons/scins';
import { Popover } from '@mui/material';
import Tooltip from 'src/components/UI/Tooltip/Tooltip';
import { ReactComponent as UserAvatarIcon } from 'src/assets/img/icons/user-avatar-icon.svg';
import { ReactComponent as PlanNotificationIcon } from 'src/assets/img/icons/plan-notification-icon.svg';
import { ReactComponent as UserLoginIcon } from 'src/assets/img/icons/user-icon.svg';
import { ReactComponent as NetworkIcon } from 'src/assets/img/icons/network-icon.svg';
import { ReactComponent as GlobeIcon } from 'src/assets/img/icons/globe-icon.svg';
import { ReactComponent as SubtaskIcon } from 'src/assets/img/icons/subtask-icon.svg';
import { ReactComponent as PinWorldIcon } from 'src/assets/img/icons/pin-world-icon.svg';
import { ReactComponent as CodeBranchIcon } from 'src/assets/img/icons/code-branch-icon.svg';
import { ReactComponent as DesktopIcon } from 'src/assets/img/icons/desktop-icon.svg';
import calculateRemainingDays from '../../../../utils/helpers/calculateRemainingDays';

const UserDropdown = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'user-dropdown' });
  const { t: th } = useTranslation('translation', { keyPrefix: 'header' });

  const { languageTypesMap } = useEnumTranslate();

  const [showSettingsPopup, setShowSettingsPopup] = useState<boolean>(false);
  const [userInfoAnchorEl, setUserInfoAnchorEl] = useState<any | null>(null);
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const [logout] = useLogoutMutation({});
  const userData = useTypedSelector(selectors.getUser);
  const isAuth = getAuthTokens().accessToken;

  const { playLogoutSound } = useSounds();

  const logoutHandler = async () => {
    await logout({})
      .unwrap()
      .then(() => {
        playLogoutSound();
        dispatch(clearStore());
        close();
        return navigate(PAGES_ENUM.SIGN_IN);
      })
      .catch((e) => toast.error(e?.data?.message || 'logout fail'));
  };
  const [anchorEl, setAnchorEl] = React.useState<any | null>(null);

  const handleClick = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleUserInfoClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    setUserInfoAnchorEl(event.currentTarget);
  };

  const handleUserInfoClose = () => {
    setUserInfoAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const userInfoOpen = Boolean(userInfoAnchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <>
      <Tooltip title={th('t-title-profile')}>
        <UserIcon
          className={'header-icon stroke-path'}
          aria-describedby="user-dropdown"
          onClick={handleClick}
          data-qa-selector="user-icon-button"
        />
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        sx={{
          '.MuiPaper-root': {
            marginTop: '10px',
            width: '308px',
            borderRadius: '8px',
            background: '#FFF',
            padding: '20px',
            boxShadow: 'none',
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          root: {
            style: {
              zIndex: 21,
            },
          },
        }}
      >
        <div className={cls['user-dropdown']}>
          <div className={cls['user-info-box']} onClick={handleUserInfoClick}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#000',
              }}
              className="flex-center"
            >
              <UserAvatarIcon />
            </div>
            <div className="d-flex flex-col">
              <p className={cls['user-info-name']}>{userData?.full_name}</p>
              <p className={cls['user-info-role']}>{userData?.role}</p>
            </div>
          </div>
          {userData?.is_superuser && userData?.subscription ? (
            <div className={cls['plan-info-box']}>
              <div className="align-center">
                <PlanNotificationIcon />
                <p className={cls['plan-name']}>
                  {userData.subscription?.['SubscriptionType']?.name}
                </p>
              </div>
              <p className={cls['plan-left-time']}>
                {th('t-remaining-tariff-user-dropdown')}
                <span style={{ marginLeft: '8px' }}>
                  {calculateRemainingDays(userData.subscription?.end_date)}
                </span>
              </p>

              <NavLink
                className="submit-button w-fit"
                to={PAGES_ENUM.SUBSCRIPTIONS}
                onClick={handleClose}
              >
                {th('t-connect-tariff')}
              </NavLink>
            </div>
          ) : null}
          <div className={cls.actions}>
            <div
              className={cls.action}
              onClick={() => {
                setShowSettingsPopup(true);
              }}
            >
              <div className={cls['dropdown-icon-box']}>
                <SettignsIcon
                  className={'icon'}
                  style={{ width: '20px', height: '20px' }}
                />
              </div>
              <span>{t('settings')}</span>
            </div>
            {isAuth && (
              <div
                className={cls.action}
                onClick={logoutHandler}
                data-qa-selector="user-logout-button"
              >
                <div className={cls['dropdown-icon-box']}>
                  <PowerIcon
                    className={'icon'}
                    style={{ width: '13px', height: '13px' }}
                  />
                </div>
                <span>{t('quit')}</span>
              </div>
            )}
          </div>
        </div>
      </Popover>

      <Popover
        id={id}
        open={userInfoOpen}
        anchorEl={userInfoAnchorEl}
        onClose={handleUserInfoClose}
        sx={{
          '.MuiPaper-root': {
            width: '308px',
            borderRadius: '8px',
            background: '#FFF',
            padding: '20px',
            boxShadow: 'none',
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          root: {
            style: {
              zIndex: 21,
            },
          },
        }}
      >
        <div className={cls['user-dropdown']}>
          <div className={cls.entries}>
            <div className={cls.entry}>
              <div className={cls['entry-title']}>
                <UserLoginIcon className="icon" />
                <p>{t('login')}</p>
              </div>
              <div className={`${cls['entry-value']} fw-600`}>
                {userData?.login}
              </div>
            </div>
            <div className={cls.entry}>
              <div className={cls['entry-title']}>
                <NetworkIcon className="icon" />
                <p>{t('group')} </p>
              </div>
              <div className={`${cls['entry-value']}`}>{userData?.role}</div>
            </div>
            <div className={cls.entry}>
              <div className={cls['entry-title']}>
                <GlobeIcon className="icon" />
                <p>{t('country')}</p>
              </div>
              <div className={`${cls['entry-value']}`}>
                <span>
                  {(userData?.country && userData?.country?.name?.ua) ||
                    'Украина'}
                </span>
              </div>
            </div>
            <div className={cls.entry}>
              <div className={cls['entry-title']}>
                <SubtaskIcon className="icon" />
                <p>{t('department')} </p>
              </div>
              <div className={`${cls['entry-value']}`}>
                {userData?.offices?.length
                  ? userData?.offices.map((item) => item.name).join(', ')
                  : 'no offices'}
              </div>
            </div>
            <div className={cls.entry}>
              <div className={cls['entry-title']}>
                <PinWorldIcon className="icon" />
                <p>{t('browser')}</p>
              </div>
              <div className={`${cls['entry-value']}`}>
                <span>{getBrowserVersion()}</span>
              </div>
            </div>
            <div className={cls.entry}>
              <div className={cls['entry-title']}>
                <CodeBranchIcon className="icon" />
                <p>{t('os')}</p>
              </div>
              <div className={`${cls['entry-value']}`}>
                <span>{getOC()}</span>
              </div>
            </div>
            <div className={cls.entry}>
              <div className={cls['entry-title']}>
                <DesktopIcon className="icon" />
                <p>{t('ip')}</p>
              </div>
              <div className={`${cls['entry-value']}`}>
                <span>{userData?.ip_address} </span>
                {/*<UkraineFlag className="icon" />*/}
                <span className="ml-5">
                  {' '}
                  {(userData?.country && userData?.country?.name?.ua) ||
                    'Украина'}
                </span>
              </div>
            </div>
            <div className={cls.entry}>
              <div className={cls['entry-title']}>
                <GlobeIcon className="icon" />
                <p>{t('language')}</p>
              </div>
              <div className={`${cls['entry-value']}`}>
                <span>
                  {[userData?.crm_language]} (
                  {languageTypesMap[userData?.crm_language || 'ua']})
                </span>
              </div>
            </div>
          </div>
        </div>
      </Popover>

      {showSettingsPopup && (
        <UserSettingsPopup
          show={showSettingsPopup}
          close={() => {
            setShowSettingsPopup(false);
          }}
        />
      )}
    </>
  );
};
export default UserDropdown;
